import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { BaseButton, FilterBottomSheet, useIsMobile, useT } from '@repo/common';

import { ReactComponent as VisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_visible_outline.svg';
import { ReactComponent as InvisibleIcon } from '@repo/common/assets/icons/16px/icon_16px_invisible-close_outline.svg';
import { ReactComponent as RocketIcon } from '@repo/common/assets/icons/16px/icon_16px_rocket-launch_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/16px/icon_16px_delete_outline.svg';

/**
 * 아티클 카드의 옵션 메뉴.
 *
 * <p>상태에 따라 항목이 달라진다. (Edit은 노션에서 하므로 제외)
 * <ul>
 *   <li>ACTIVE : Hide, Delete</li>
 *   <li>HIDDEN : Set as Active, Delete</li>
 *   <li>그 외(DRAFT·검토·예약) : Publish, Delete</li>
 * </ul>
 *
 * <p>데스크탑은 앵커 기준 팝오버, 모바일은 바텀시트. ResumeCardOptionList와 같은 구조다.
 */
const MENU_WIDTH = 200;

const ArticleOptionList = ({
  anchorRect,
  statusCode,
  onClose,
  onHide,
  onPublish,
  onDelete,
}) => {
  const t = useT();
  const isMobile = useIsMobile();
  const listRef = useRef(null);
  const [activeItem, setActiveItem] = useState(null);

  // 스크롤하면 앵커 위치가 어긋나므로 닫는다
  useEffect(() => {
    if (isMobile) return undefined;

    const handleScroll = () => onClose?.();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [onClose, isMobile]);

  if (!anchorRect && !isMobile) return null;

  const handleItemClick = (event, key, action) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveItem(key);
    onClose?.();
    action?.();
  };

  // 노션 옵션명이 "숨김"처럼 영문 코드가 없을 수도 있어 한글 라벨도 흡수한다
  const KOREAN_CODE = { 발행: 'ACTIVE', '발행 완료': 'ACTIVE', 숨김: 'HIDDEN' };
  const raw = String(statusCode ?? '').trim();
  const code = KOREAN_CODE[raw] ?? raw.toUpperCase();

  const items = [];

  if (code === 'ACTIVE') {
    items.push({
      key: 'hide',
      label: t('Hide', '숨기기'),
      icon: <InvisibleIcon />,
      action: onHide,
    });
  } else if (code === 'HIDDEN') {
    items.push({
      key: 'publish',
      label: t('Set as Active', '공개로 전환'),
      icon: <VisibleIcon />,
      action: onPublish,
    });
  } else {
    items.push({
      key: 'publish',
      label: t('Publish', '발행'),
      icon: <RocketIcon />,
      action: onPublish,
    });
  }

  items.push({
    key: 'delete',
    label: t('Delete', '삭제'),
    icon: <DeleteIcon />,
    action: onDelete,
  });

  const menuItems = (
    <ul className='flex flex-col'>
      {items.map((item) => (
        <li key={item.key}>
          <BaseButton
            type='text'
            size='m'
            iconPosition='after'
            icon={item.icon}
            width='100%'
            separate={true}
            leftalign={true}
            onClick={(event) => handleItemClick(event, item.key, item.action)}
            bgclass={`bg-grayscale-gray00 border border-grayscale-gray00 ${
              activeItem === item.key
                ? 'bg-grayscale-gray02 text-text-primary'
                : ''
            }`}
            textclass='typo-b14Strong'
            hoverclass='hover:bg-grayscale-gray01 hover:text-text-primary'
            activeclass='active:bg-grayscale-gray02 active:text-text-primary'
          >
            {item.label}
          </BaseButton>
        </li>
      ))}
    </ul>
  );

  if (isMobile) {
    return ReactDOM.createPortal(
      <FilterBottomSheet onClose={onClose}>{menuItems}</FilterBottomSheet>,
      document.body,
    );
  }

  // 화면 오른쪽 밖으로 나가지 않게 클램프한다
  const left = Math.max(
    8,
    Math.min(anchorRect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
  );

  return ReactDOM.createPortal(
    <>
      <div
        className='fixed inset-0 z-[999999] bg-transparent'
        onClick={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
      />

      <div
        ref={listRef}
        onClick={(event) => event.stopPropagation()}
        style={{
          position: 'fixed',
          top: anchorRect.bottom + 8,
          left,
          width: MENU_WIDTH,
        }}
        className='
          z-[1000000] rounded-lg border border-grayscale-gray02
          bg-grayscale-gray00 shadow-popup-04
        '
      >
        <div className='rounded-lg bg-grayscale-gray00 p-2'>{menuItems}</div>
      </div>
    </>,
    document.body,
  );
};

export default ArticleOptionList;
